"use client";
//
// Quick filter bar: pick a column, enter a value, add as a chip.
// Default filter template can be supplied by the active preset.

import type { ColumnDef } from "@tanstack/react-table";
import { Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type {
  FilterClause,
  SelectList,
  SmartTableDefaultFilter,
  TextOperator,
} from "../domain";
import { FILTER_TYPE_OBJ } from "../domain";
import { buildFilterClause, operatorToClause, type SelectFilterOperator } from "../app";
import { getColumnName, resolveFilterType } from "./column-helpers";
import { FilterInput } from "./filter-input";
import { useTableLabelFn } from "./labels-context";
import { useTableComponents } from "./table-provider";

interface FilterBarProps<TData> {
  columns: ColumnDef<TData>[];
  lists?: SelectList[] | undefined;
  filters: FilterClause[];
  onFiltersChange: (filters: FilterClause[]) => void;
  defaultFilter?: SmartTableDefaultFilter | undefined;
}

function getColumnByName<TData>(
  columns: ColumnDef<TData>[],
  name: string,
): ColumnDef<TData> | undefined {
  return columns.find((c) => getColumnName(c) === name);
}

export function FilterBar<TData>({
  columns,
  lists,
  filters,
  onFiltersChange,
  defaultFilter,
}: FilterBarProps<TData>) {
  const t = useTableLabelFn();
  const { Button, Select } = useTableComponents();
  const [selectedColumn, setSelectedColumn] = useState<string>("");
  const [draftValue, setDraftValue] = useState<string>("");
  const [draftOperator, setDraftOperator] = useState<TextOperator>("contains");
  const [draftMin, setDraftMin] = useState<string>("");
  const [draftMax, setDraftMax] = useState<string>("");
  const [draftFrom, setDraftFrom] = useState<string>("");
  const [draftTo, setDraftTo] = useState<string>("");
  const [draftSelect, setDraftSelect] = useState<string>("");
  const [draftBool, setDraftBool] = useState<boolean>(true);
  const [draftMultiValues, setDraftMultiValues] = useState<string[]>([]);
  const [draftSelectOperator, setDraftSelectOperator] =
    useState<SelectFilterOperator>("in");

  const filterableColumns = useMemo(
    () =>
      columns.filter((c) => {
        const meta = c.meta;
        return meta?.filterable !== "none";
      }),
    [columns],
  );

  // Find a sensible default column when none is selected.
  const firstTextColumn = useMemo(() => {
    return (
      filterableColumns.find(
        (c) => resolveFilterType(c, lists) === FILTER_TYPE_OBJ.TEXT,
      ) ?? filterableColumns[0]
    );
  }, [filterableColumns]);

  // Sync draft column/operator with defaultFilter prop or fallback.
  useEffect(() => {
    const targetName =
      defaultFilter?.column ??
      (firstTextColumn ? getColumnName(firstTextColumn) : "");
    if (!targetName) return;
    if (selectedColumn && selectedColumn !== targetName) return; // respect user choice
    const col = getColumnByName(filterableColumns, targetName);
    const type = col ? resolveFilterType(col, lists) : null;
    if (selectedColumn !== targetName) setSelectedColumn(targetName);
    if (type === FILTER_TYPE_OBJ.TEXT && defaultFilter?.operator) {
      setDraftOperator(defaultFilter.operator);
    }
  }, [defaultFilter, firstTextColumn, filterableColumns, selectedColumn]);

  const selectedColumnDef = useMemo(
    () => getColumnByName(filterableColumns, selectedColumn),
    [filterableColumns, selectedColumn],
  );

  const columnOptions = useMemo(
    () =>
      filterableColumns.map((c) => {
        const name = getColumnName(c);
        const header = String(c.header ?? name);
        return { value: name, label: header };
      }),
    [filterableColumns],
  );

  const resetDraft = () => {
    setDraftValue("");
    setDraftOperator("contains");
    setDraftMin("");
    setDraftMax("");
    setDraftFrom("");
    setDraftTo("");
    setDraftSelect("");
    setDraftBool(true);
    setDraftMultiValues([]);
    setDraftSelectOperator("in");
  };

  const buildClause = (): FilterClause | null => {
    if (!selectedColumnDef) return null;
    const type = resolveFilterType(selectedColumnDef, lists);
    if (!type) return null;
    // For list-type columns, use the operator model
    if (type === "select-single" || type === "select-multi") {
      return operatorToClause(
        selectedColumn,
        draftSelectOperator,
        draftSelect,
        draftMultiValues,
      );
    }
    return buildFilterClause({
      column: selectedColumn,
      filterType: type,
      draftValue,
      draftOperator,
      draftMin,
      draftMax,
      draftFrom,
      draftTo,
      draftSelect,
      draftBool,
      draftMultiValues,
      draftNegate: false,
    });
  };

  const handleAdd = () => {
    const clause = buildClause();
    if (!clause) return;
    const existing = filters.find((f) => f.column === clause.column);
    if (existing) {
      onFiltersChange(
        filters.map((f) => (f.column === clause.column ? clause : f)),
      );
    } else {
      onFiltersChange([...filters, clause]);
    }
    resetDraft();
  };

  const handleClear = () => {
    onFiltersChange([]);
    resetDraft();
  };

  const handleColumnChange = (name: string) => {
    setSelectedColumn(name);
    resetDraft();
    const col = getColumnByName(filterableColumns, name);
    const type = col ? resolveFilterType(col, lists) : null;
    if (type === FILTER_TYPE_OBJ.TEXT) {
      setDraftOperator(defaultFilter?.operator ?? "contains");
    }
  };

  const valueInput = selectedColumnDef ? (
    <FilterInput
      column={selectedColumnDef}
      lists={lists}
      draftValue={draftValue}
      setDraftValue={setDraftValue}
      draftOperator={draftOperator}
      setDraftOperator={setDraftOperator}
      draftMin={draftMin}
      setDraftMin={setDraftMin}
      draftMax={draftMax}
      setDraftMax={setDraftMax}
      draftFrom={draftFrom}
      setDraftFrom={setDraftFrom}
      draftTo={draftTo}
      setDraftTo={setDraftTo}
      draftSelect={draftSelect}
      setDraftSelect={setDraftSelect}
      draftBool={draftBool}
      setDraftBool={setDraftBool}
      draftMultiValues={draftMultiValues}
      setDraftMultiValues={setDraftMultiValues}
      draftSelectOperator={draftSelectOperator}
      setDraftSelectOperator={setDraftSelectOperator}
      onAdd={handleAdd}
    />
  ) : null;

  return (
    <div
      className="filterBar flex flex-wrap items-center gap-2"
      aria-label={t("table.filters.bar.aria")}
    >
      <Search className="h-4 w-4 text-text-secondary" />
      <Select
        value={selectedColumn}
        onChange={handleColumnChange}
        placeholder={t("table.filters.selectColumn")}
        ariaLabel={t("table.filters.aria.columnSelect")}
        className="w-auto min-w-[160px]"
        options={columnOptions}
        searchable
      />
      {valueInput && <div className="min-w-[200px]">{valueInput}</div>}
      <Button
        variant="outline"
        size="sm"
        onClick={handleAdd}
        disabled={!selectedColumn || !buildClause()}
      >
        {t("table.filters.addBtn")}
      </Button>
      {filters.length > 0 && (
        <button
          onClick={handleClear}
          className="text-text-secondary hover:text-text-primary"
          aria-label={t("table.filters.clearAll")}
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {filters.length > 0 && (
        <span className="text-xs text-text-secondary">
          {filters.length} {t("table.filters")}
        </span>
      )}
    </div>
  );
}
